import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useSession } from '@/hooks/useSession';
import { createOrGetAddress, getMyAddresses, type LiquidationAddress } from '@/lib/rpc/address';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { EmptyState, Page, PageHeader, PageContent } from '@supabank/ui';
import { ErrorState } from '@/ui/status/ErrorState';
import { UX_LEXICON } from '@/lib/ux-lexicon';
import { trackUX, UX_EVENTS } from '@/lib/analytics/ux-events';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function AddressPage() {
  const { status } = useSession();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [address, setAddress] = useState<LiquidationAddress | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Redirect unauthenticated users
  useEffect(() => {
    if (status === 'anon') {
      navigate('/auth');
    }
  }, [status, navigate]);

  // Load existing address on mount
  useEffect(() => {
    if (status === 'authed') {
      loadExistingAddress();
    }
  }, [status]);

  const loadExistingAddress = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getMyAddresses();
      if (result.ok && result.data && result.data.length > 0) {
        setAddress(result.data[0]);
      }
    } catch (err) {
      // TASK-009C: Use ErrorState instead of console.error
      setError(err instanceof Error ? err : new Error('Failed to load address'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAddress = async () => {
    setCreating(true);
    setError(null);
    try {
      const result = await createOrGetAddress({ asset: 'USDT', network: 'ETH' });
      if (result.ok && result.data) {
        setAddress(result.data);
        // TASK-009D: Track address_created event
        trackUX(UX_EVENTS.ADDRESS_CREATED, {
          asset: result.data.asset || 'USDT',
          network: result.data.network || 'ETH',
          source: 'address_page',
        });
        toast({
          title: 'Address generated',
          description: 'Your crypto wallet is ready to use.',
        });
      } else {
        // TASK-009C: Use ErrorState instead of toast for errors
        setError(new Error('Failed to create address. Please try again or contact support.'));
      }
    } catch (error) {
      // TASK-009C: Use ErrorState instead of console.error and toast
      setError(error instanceof Error ? error : new Error('Failed to create address. Please try again.'));
    } finally {
      setCreating(false);
    }
  };

  const handleCopyAddress = async () => {
    if (address?.address) {
      try {
        await navigator.clipboard.writeText(address.address);
        // TASK-009D: Track address_copied event
        trackUX(UX_EVENTS.ADDRESS_COPIED, {
          asset: address.asset || 'USDT',
          network: address.network || 'ETH',
          source: 'address_page',
        });
        toast({
          title: 'Copied!',
          description: 'Address copied to clipboard.',
        });
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Copy failed',
          description: 'Please copy the address manually.',
        });
      }
    }
  };

  // Show loading state for auth check
  if (status === 'loading' || status === 'recovering') {
    return (
      <Page data-ux-contract="canon">
        <PageContent>
          <Skeleton className={cn('h-[300px] w-full', semanticTokens.radius.lg)} />
        </PageContent>
      </Page>
    );
  }

  // Don't render anything for anonymous users (they'll be redirected)
  if (status === 'anon') {
    return null;
  }

  return (
    <Page data-ux-contract="canon">
      <PageHeader
        icon={sfIcons.wallet}
        title="Your crypto wallet"
        subtitle="Receive crypto payments that will be automatically converted to cash"
      />

      <PageContent>
        {error ? (
          <ErrorState
            error={error}
            errorType="wallet_addresses_failed"
            surface="address"
            onRetry={loadExistingAddress}
            title={UX_LEXICON.address.error.title}
            message={UX_LEXICON.address.error.message}
            retryLabel={UX_LEXICON.address.error.retry}
          />
        ) : loading ? (
          <Skeleton className={cn('h-[300px] w-full', semanticTokens.radius.lg)} />
        ) : address ? (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <SFIcon icon={sfIcons.wallet} className="h-5 w-5" />
                <CardTitle>Your Address</CardTitle>
                <Badge variant="secondary">{address.network}</Badge>
              </div>
              <CardDescription>
                Send {address.asset} to this address for automatic conversion to cash
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Address</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-muted rounded border text-sm font-mono break-all">
                    {address.address}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyAddress}
                    className="shrink-0"
                  >
                    <SFIcon icon={sfIcons.copy} className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Asset:</span>
                  <div className="font-medium">{address.asset}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Network:</span>
                  <div className="font-medium">{address.network}</div>
                </div>
              </div>

              <div className="p-4 bg-primary/5 border border-primary/20 rounded">
                <p className="text-sm">
                  <strong>How it works:</strong> Send crypto to this address and it will be automatically
                  converted to your preferred currency and sent to your connected bank account.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <EmptyState
            icon={sfIcons.wallet}
            title={UX_LEXICON.address.empty.title}
            description={UX_LEXICON.address.empty.description}
            action={{
              label: creating ? 'Generating...' : UX_LEXICON.address.empty.action.generate,
              onClick: handleCreateAddress,
            }}
          />
        )}
      </PageContent>
    </Page>
  );
}